/**
 * 🚀 [Phase v13.56 — 2026-07-22] Section "Statut des signatures" — vue globale.
 *
 * Remplace l'ancien onglet "Signatures en attente" qui filtrait uniquement
 * les employés à re-signer. Affiche maintenant TOUS les employés actifs
 * avec leur statut individuel (signé / en attente / expire bientôt).
 *
 * Actions par employé :
 *   - Non signé : Envoyer email · Notifier in-app · Envoyer les deux
 *   - Signé : Voir le contrat signé (PDF) · Voir l'historique
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle, Clock, AlertTriangle, Mail, Bell, MailPlus, Eye, History,
  Users, Loader2, Filter
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

const API_URL = (typeof window !== "undefined" ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || "";

const STATUT_META = {
  signe: { label: "Signé", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  en_attente: { label: "En attente", color: "bg-orange-100 text-orange-800", icon: Clock },
  expire_bientot: { label: "Expire bientôt", color: "bg-red-100 text-red-800", icon: AlertTriangle },
};

export function SectionSignaturesGlobal({ onViewHistory, onViewContract }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filterStatut, setFilterStatut] = useState("tous");
  const [filterRole, setFilterRole] = useState("tous");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/contracts/all-signatures-status`);
      setData(r.data);
    } catch (e) {
      toast.error(`Erreur chargement statuts : ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rolesUniques = useMemo(() => {
    if (!data?.employees) return [];
    const s = new Set();
    data.employees.forEach(e => { if (e.role) s.add(e.role); });
    return Array.from(s).sort();
  }, [data]);

  const filteredEmployees = useMemo(() => {
    if (!data?.employees) return [];
    let out = data.employees;
    if (filterStatut !== "tous") out = out.filter(e => e.statut === filterStatut);
    if (filterRole !== "tous") out = out.filter(e => e.role === filterRole);  // E3b-whitelist : filtre de données
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(e =>
        e.nom_complet.toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [data, filterStatut, filterRole, search]);

  const sendEmail = async (emp) => {
    setBusyId(`${emp.employee_id}-email`);
    try {
      await axios.post(`${API_URL}/api/contracts/pending-signatures/${emp.employee_id}/send-email`);
      toast.success(`Courriel envoyé à ${emp.nom_complet}`);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const sendNotif = async (emp) => {
    setBusyId(`${emp.employee_id}-notif`);
    try {
      await axios.post(`${API_URL}/api/contracts/pending-signatures/${emp.employee_id}/send-notification`);
      toast.success(`Notification créée pour ${emp.nom_complet}`);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const sendBoth = async (emp) => {
    setBusyId(`${emp.employee_id}-both`);
    try {
      await Promise.all([
        axios.post(`${API_URL}/api/contracts/pending-signatures/${emp.employee_id}/send-email`),
        axios.post(`${API_URL}/api/contracts/pending-signatures/${emp.employee_id}/send-notification`),
      ]);
      toast.success(`Courriel + notification envoyés à ${emp.nom_complet}`);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="section-signatures-global">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Total actifs</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="sig-stat-total">{data?.total || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Signés</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="sig-stat-signes">{data?.signes || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">En attente</p>
          <p className="text-2xl font-bold text-orange-600" data-testid="sig-stat-en-attente">{data?.en_attente || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Expire bientôt</p>
          <p className="text-2xl font-bold text-red-600" data-testid="sig-stat-expire">{data?.expire_bientot || 0}</p>
        </CardContent></Card>
      </div>

      {/* Cycle actif */}
      {data?.template_actif && (
        <div className="flex items-center gap-2 text-sm px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid="sig-template-actif">
          <CheckCircle className="w-4 h-4 text-blue-600" />
          <span className="text-blue-900">Cycle actif :</span>
          <strong className="text-blue-900">{data.template_actif.titre}</strong>
          <Badge variant="outline" className="text-xs">v{data.template_actif.version}</Badge>
          {data.template_actif.source_type === "upload_docx" && (
            <Badge className="bg-purple-100 text-purple-800 text-xs">Uploadé (.docx)</Badge>
          )}
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Rechercher un employé..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1"
              data-testid="sig-search-input"
            />
            {rolesUniques.length > 0 && (
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="sig-filter-role"
              >
                <option value="tous">Tous les rôles</option>
                {rolesUniques.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>

          <Tabs value={filterStatut} onValueChange={setFilterStatut}>
            <TabsList className="grid grid-cols-4 w-full sm:w-auto">
              <TabsTrigger value="tous" data-testid="sig-tab-tous">Tous ({data?.total || 0})</TabsTrigger>
              <TabsTrigger value="signe" data-testid="sig-tab-signe">Signés ({data?.signes || 0})</TabsTrigger>
              <TabsTrigger value="en_attente" data-testid="sig-tab-en-attente">En attente ({data?.en_attente || 0})</TabsTrigger>
              <TabsTrigger value="expire_bientot" data-testid="sig-tab-expire">Expire ({data?.expire_bientot || 0})</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Liste employés */}
      {filteredEmployees.length === 0 ? (
        <Card><CardContent className="pt-6">
          <p className="text-sm text-slate-500 text-center py-4" data-testid="sig-empty">
            Aucun employé ne correspond aux filtres.
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="sig-employees-list">
          {filteredEmployees.map(emp => {
            const meta = STATUT_META[emp.statut] || STATUT_META.en_attente;
            const StatIcon = meta.icon;
            const rowBusy = busyId?.startsWith(`${emp.employee_id}-`);
            return (
              <Card
                key={emp.employee_id}
                className="transition-all hover:shadow-md"
                data-testid={`sig-employee-${emp.employee_id}`}
              >
                <CardContent className="pt-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <EmployeeAvatar
                        photoUrl={emp.photo_url}
                        prenom={emp.prenom}
                        nom={emp.nom}
                        size="md"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{emp.nom_complet}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                          {emp.role && <Badge variant="outline" className="text-xs">{emp.role}</Badge>}
                          <Badge variant={emp.permanence === "saisonnier" ? "default" : "secondary"} className="text-xs">
                            {emp.permanence === "saisonnier" ? "Saisonnier" : "Permanent"}
                          </Badge>
                          {emp.historique_count > 0 && (
                            <span className="text-[11px] text-slate-500">
                              {emp.historique_count} contrat{emp.historique_count > 1 ? "s" : ""} dans l&apos;historique
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-shrink-0">
                      <Badge className={`${meta.color} gap-1`} data-testid={`sig-statut-${emp.employee_id}`}>
                        <StatIcon className="w-3 h-3" /> {meta.label}
                      </Badge>
                      {emp.date_signature && (
                        <span className="text-[11px] text-slate-500">
                          {new Date(emp.date_signature).toLocaleDateString("fr-CA")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                    {emp.statut === "en_attente" || emp.statut === "expire_bientot" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendEmail(emp)}
                          disabled={rowBusy || !emp.email}
                          className="gap-1.5"
                          data-testid={`sig-btn-email-${emp.employee_id}`}
                        >
                          {busyId === `${emp.employee_id}-email` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Mail className="w-3.5 h-3.5" />
                          )}
                          Courriel
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendNotif(emp)}
                          disabled={rowBusy}
                          className="gap-1.5"
                          data-testid={`sig-btn-notif-${emp.employee_id}`}
                        >
                          {busyId === `${emp.employee_id}-notif` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Bell className="w-3.5 h-3.5" />
                          )}
                          Notif in-app
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => sendBoth(emp)}
                          disabled={rowBusy || !emp.email}
                          className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                          data-testid={`sig-btn-both-${emp.employee_id}`}
                        >
                          {busyId === `${emp.employee_id}-both` ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <MailPlus className="w-3.5 h-3.5" />
                          )}
                          Envoyer les deux
                        </Button>
                        {!emp.email && (
                          <span className="text-[11px] text-red-500">⚠ pas de courriel</span>
                        )}
                      </>
                    ) : (
                      <>
                        {emp.signature_id && emp.signature_pdf_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onViewContract && onViewContract(emp)}
                            className="gap-1.5"
                            data-testid={`sig-btn-view-${emp.employee_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" /> Voir le contrat signé
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewHistory && onViewHistory(emp)}
                          className="gap-1.5"
                          data-testid={`sig-btn-history-${emp.employee_id}`}
                        >
                          <History className="w-3.5 h-3.5" /> Historique
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SectionSignaturesGlobal;
